import { Ban, CheckCircle2, ClipboardCheck, FileClock, RotateCcw, UserMinus, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ComponentType } from 'react';
import { useParams } from 'react-router-dom';

import { usePermission } from '../../../../auth/AuthContext';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { NextActionAlert } from '../../../../components/NextActionAlert';
import { PreviewTable } from '../../../../components/PreviewTable';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell, ProfileHeaderSkeleton } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime, toIso } from '../../../../lib/formatDate';
import { saveBlob } from '../../../../lib/saveBlob';
import { usePersonNames } from '../../personnel/usePersonNames';
import { attritionApi } from '../attritionApi';
import {
  ATTRITION_CLEARANCE,
  ATTRITION_REVIEW,
  ATTRITION_STATUS,
  type AttritionRequestClearance,
  type AttritionRequestDetail,
  type AttritionRequestDocument,
} from '../attritionTypes';
import {
  CancelAttritionRequestModal,
  DecideAttritionClearanceModal,
  ReviewAttritionDocumentModal,
  UploadAttritionDocumentModal,
} from '../components/AttritionActionModals';
import { AttritionClearancesCard } from '../components/AttritionClearancesCard';
import { AttritionDocumentPreviewModal } from '../components/AttritionDocumentPreviewModal';
import { AttritionDocumentsCard } from '../components/AttritionDocumentsCard';
import { AttritionRequestModal } from '../components/AttritionRequestModal';

type TabKey = 'info' | 'documents' | 'clearances';

/** What is open: a form that COLLECTS, or a dialog that only confirms. One at a time. */
type Pending =
  | { kind: 'edit' }
  | { kind: 'cancel' }
  | { kind: 'delete' }
  | { kind: 'restore' }
  | { kind: 'upload'; document: AttritionRequestDocument }
  | { kind: 'review'; document: AttritionRequestDocument; decision: 'approve' | 'return' }
  | { kind: 'preview'; document: AttritionRequestDocument }
  | { kind: 'decide'; clearance: AttritionRequestClearance; decision: 'clear' | 'not-cleared' }
  | null;

/**
 * One attrition request, on the header layout — it reads as one continuous thing: who is leaving,
 * under which category, and the documents that separation needs.
 *
 * **Info · Documents.** Info carries the request's own fields; Documents is the checklist with its
 * upload, preview and review controls. It lands on Info, the first declared tab — the banner says
 * what is waiting and takes the reader to Documents when there is something to do there.
 *
 * **Cancelling is the record-level lifecycle action**, so it is the banner's; delete is in the cog.
 */
export function AttritionRequestProfilePage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const canReadPersonnel = usePermission('personnel.read');

  const [request, setRequest] = useState<AttritionRequestDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const personId = request?.personId;
  const personIds = useMemo(() => (personId != null ? [personId] : []), [personId]);
  const names = usePersonNames(canReadPersonnel ? personIds : []);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      attritionApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) {
            setRequest(next);
            setError(null);
          }
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this request.', 0));
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  const breadcrumb: BreadcrumbSegment[] = [
    'Human Resources',
    'Attrition',
    // The way back is the request's own CATEGORY — the sidebar's entry it was opened from.
    request
      ? { label: request.categoryName, to: `/attrition/categories/${request.attritionCategoryId}` }
      : { label: 'Attrition', to: '/attrition' },
    request ? request.requestNumber : 'Request',
  ];

  // Guarded on having NOTHING to show — a failed refresh keeps the record and goes to the banner.
  // A 403 here is the employee's clearance, and the server's message says so.
  if (error && !request) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ProfileHeaderSkeleton tabs={3} />
      </div>
    );
  }

  const record = request;
  const employeeName = !canReadPersonnel
    ? 'Hidden'
    : record.personId == null
      ? 'Restricted'
      : names.statusOf(record.personId) === 'erased'
        ? 'Identity erased'
        : (names.nameOf(record.personId) ?? (names.failed ? 'Name unavailable' : null));

  const closedReason = record.isOpen
    ? null
    : !record.isActive
      ? 'This request is deleted. Restore it before changing it.'
      : `This request is ${record.statusLabel.toLowerCase()} and can no longer be changed.`;

  function saved(next: AttritionRequestDetail) {
    setRequest(next);
    setPending(null);
  }

  async function runConfirmed() {
    if (pending?.kind !== 'delete' && pending?.kind !== 'restore') return;

    setActing(true);
    setActionError(null);

    try {
      if (pending.kind === 'delete') await attritionApi.remove(record.id);
      else await attritionApi.restore(record.id);

      toast.success(`${record.requestNumber} was ${pending.kind === 'delete' ? 'deleted' : 'restored'}.`);
      setPending(null);
      load();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setActing(false);
    }
  }

  async function download(document: AttritionRequestDocument) {
    try {
      const { blob } = await attritionApi.downloadDocument(record.id, document.id);
      saveBlob(blob, document.fileName ?? document.name);
    } catch (caught) {
      toast.failure(caught instanceof ApiError ? caught.message : 'Could not download that document.');
    }
  }

  const banner = bannerFor(record);
  const awaitingMe = record.documents.filter((d) => d.isMine && d.reviewStatus === ATTRITION_REVIEW.forReview).length;
  const clearancesAwaitingMe = record.clearances.filter(
    (c) => c.isMine && c.status !== ATTRITION_CLEARANCE.cleared,
  ).length;
  // Only a RESOLVED name may be the title — "Hidden" or "Restricted" in that slot would read as the person's name.
  const resolvedName =
    canReadPersonnel && record.personId != null && names.statusOf(record.personId) !== 'erased'
      ? (names.nameOf(record.personId) ?? null)
      : null;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="attrition-request-profile"
        header={
          <ProfileHeader
            id="attrition-request-profile-header"
            icon={<UserMinus className="h-6 w-6" aria-hidden="true" />}
            /*
              THE EMPLOYEE IS THE TITLE (product call, 30 Oct 2026) — a separation is read by who is
              leaving — with the reference number and the category under it. Until the name has
              resolved, or where it is withheld, the number holds the title and is not repeated.
            */
            title={resolvedName ?? record.requestNumber}
            meta={resolvedName ? [record.requestNumber, record.categoryName] : [record.categoryName]}
            status={
              <span className="flex items-center gap-2">
                <StatusText tone="inactive">{record.statusLabel}</StatusText>
                <StatusText tone={record.isActive ? 'active' : 'inactive'}>
                  {record.isActive ? 'Active' : 'Deleted'}
                </StatusText>
              </span>
            }
            createdBy={record.createdByName}
            createdAt={record.createdAt}
            // No Edit here: the Request card's cog owns the one modal, and a record offers it once.
            settings={{
              onHistory: () => setHistoryOpen(true),
              onDelete: () => setPending({ kind: 'delete' }),
              deletePermission: 'attrition-requests.delete',
              deleteDisabledReason: !record.isActive
                ? 'This request is already deleted.'
                : record.status === ATTRITION_STATUS.completed
                  ? 'A completed request is the record of the separation and cannot be deleted.'
                  : null,
            }}
          />
        }
        banner={
          <NextActionAlert
            error={Boolean(error)}
            id="attrition-request-banner"
            icon={<banner.icon className="h-4 w-4" aria-hidden="true" />}
            title={error ? 'This page may be out of date' : banner.title}
            description={error ? error.message : banner.description}
            action={
              !record.isActive ? (
                <PermissionGate permission="attrition-requests.delete">
                  <Button
                    id="attrition-request-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => setPending({ kind: 'restore' })}
                    disabled={acting}
                  >
                    Restore
                  </Button>
                </PermissionGate>
              ) : record.isOpen ? (
                <span className="flex items-center gap-2">
                  {/* A signatory with nothing to review is sent to the clearances instead — the tab they act on. */}
                  {clearancesAwaitingMe > 0 && awaitingMe === 0 && tab !== 'clearances' ? (
                    <Button
                      id="attrition-request-open-clearances"
                      variant="primary"
                      leading={<ClipboardCheck className="h-4 w-4" aria-hidden="true" />}
                      onClick={() => setTab('clearances')}
                    >
                      Decide clearances
                    </Button>
                  ) : tab !== 'documents' && !(clearancesAwaitingMe > 0 && awaitingMe === 0) ? (
                    <Button
                      id="attrition-request-open-documents"
                      variant={awaitingMe > 0 ? 'primary' : 'secondary'}
                      leading={<ClipboardCheck className="h-4 w-4" aria-hidden="true" />}
                      onClick={() => setTab('documents')}
                    >
                      {awaitingMe > 0 ? 'Review documents' : 'Documents'}
                    </Button>
                  ) : null}
                  <PermissionGate permission="attrition-requests.update">
                    <Button
                      id="attrition-request-cancel"
                      variant="secondary"
                      leading={<XCircle className="h-4 w-4" aria-hidden="true" />}
                      onClick={() => setPending({ kind: 'cancel' })}
                    >
                      Cancel request
                    </Button>
                  </PermissionGate>
                </span>
              ) : null
            }
          />
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'documents',
            label: (
              <>
                Documents <CountBadge count={record.documents.length} />
              </>
            ),
          },
          {
            key: 'clearances',
            label: (
              <>
                Clearances <CountBadge count={record.clearances.length} />
              </>
            ),
          },
        ]}
        activeTab={tab}
        onTabChange={(next) => setTab(next as TabKey)}
      >
        {tab === 'clearances' ? (
          <AttritionClearancesCard
            record={record}
            busy={acting}
            onDecide={(clearance, decision) => setPending({ kind: 'decide', clearance, decision })}
          />
        ) : tab === 'documents' ? (
          <AttritionDocumentsCard
            record={record}
            busy={acting}
            onUpload={(document) => setPending({ kind: 'upload', document })}
            onOpen={(document) => setPending({ kind: 'preview', document })}
            onReview={(document, decision) => setPending({ kind: 'review', document, decision })}
          />
        ) : (
          <>
            <ProfileCard
              id="attrition-request-profile-request"
              title="Request"
              onEdit={() => setPending({ kind: 'edit' })}
              editPermission="attrition-requests.update"
              editDisabledReason={closedReason}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Request number', value: record.requestNumber, mono: true },
                  { label: 'Employee', value: employeeName },
                  { label: 'Employee number', value: canReadPersonnel ? record.employeeNumber : null, mono: true },
                  { label: 'Category', value: record.categoryName },
                  {
                    label: 'Effective date',
                    value: record.effectiveDate != null ? formatDateOnly(record.effectiveDate) : null,
                    mono: true,
                  },
                  { label: 'Remarks', value: record.remarks },
                ]}
              />
            </ProfileCard>

            {record.status === ATTRITION_STATUS.cancelled ? (
              // State moved by an ACTION, not typing — one of the cards that legitimately has no Edit.
              <ProfileCard id="attrition-request-profile-cancellation" title="Cancellation">
                <PreviewTable
                  variant="table-display"
                  rows={[
                    { label: 'Reason', value: record.cancelReason },
                    { label: 'Cancelled by', value: record.cancelledByName },
                    {
                      label: 'Cancelled',
                      value:
                        record.cancelledAt != null ? (
                          <time dateTime={toIso(record.cancelledAt)} className="font-mono">
                            {formatDateTime(record.cancelledAt)}
                          </time>
                        ) : null,
                    },
                  ]}
                />
              </ProfileCard>
            ) : null}

            {/* Audit is never editable: created and updated are a record of what happened. */}
            <ProfileCard id="attrition-request-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: record.createdByName },
                  {
                    label: 'Created',
                    value: (
                      <time dateTime={toIso(record.createdAt)} className="font-mono">
                        {formatDateTime(record.createdAt)}
                      </time>
                    ),
                  },
                  { label: 'Updated by', value: record.updatedByName },
                  {
                    label: 'Updated',
                    value:
                      record.updatedAt != null ? (
                        <time dateTime={toIso(record.updatedAt)} className="font-mono">
                          {formatDateTime(record.updatedAt)}
                        </time>
                      ) : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        )}
      </ProfileHeaderShell>

      {pending?.kind === 'edit' ? (
        <AttritionRequestModal
          open
          editing={record}
          employeeName={employeeName}
          onClose={() => setPending(null)}
          onSaved={saved}
        />
      ) : null}

      {pending?.kind === 'cancel' ? (
        <CancelAttritionRequestModal
          request={record}
          onClose={() => setPending(null)}
          onSaved={(next) => {
            saved(next);
            toast.success(`${record.requestNumber} was cancelled.`);
          }}
        />
      ) : null}

      {pending?.kind === 'upload' ? (
        <UploadAttritionDocumentModal
          request={record}
          document={pending.document}
          onClose={() => setPending(null)}
          onSaved={(next) => {
            saved(next);
            toast.success(`${pending.document.name} was submitted for review.`);
          }}
        />
      ) : null}

      {pending?.kind === 'review' ? (
        <ReviewAttritionDocumentModal
          request={record}
          document={pending.document}
          decision={pending.decision}
          onClose={() => setPending(null)}
          onSaved={(next) => {
            saved(next);
            toast.success(`${pending.document.name} was ${pending.decision === 'approve' ? 'approved' : 'returned'}.`);
          }}
        />
      ) : null}

      {pending?.kind === 'decide' ? (
        <DecideAttritionClearanceModal
          request={record}
          clearance={pending.clearance}
          decision={pending.decision}
          onClose={() => setPending(null)}
          onSaved={(next) => {
            saved(next);
            toast.success(
              pending.decision === 'clear'
                ? `${pending.clearance.orgUnitName} cleared the employee.`
                : `${pending.clearance.orgUnitName} recorded the employee as not cleared.`,
            );
          }}
        />
      ) : null}

      {pending?.kind === 'preview' ? (
        <AttritionDocumentPreviewModal
          requestId={record.id}
          document={pending.document}
          onDownload={() => void download(pending.document)}
          onClose={() => setPending(null)}
        />
      ) : null}

      {pending?.kind === 'delete' || pending?.kind === 'restore' ? (
        <ConfirmDialog
          open
          onClose={() => {
            setPending(null);
            setActionError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={pending.kind === 'delete' ? 'Delete request' : 'Restore request'}
          danger={pending.kind === 'delete'}
          requireConfirm
          requireCredential={pending.kind === 'delete'}
          loading={acting}
          id={`attrition-request-profile-${pending.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              {pending.kind === 'delete' ? (
                <>
                  <p>{record.requestNumber} is hidden from the register.</p>
                  <p>
                    <strong>This is not the same as cancelling it.</strong> Deleting is for a request filed against the
                    wrong employee, or twice. Nothing is erased — the documents stay, and it can be restored.
                  </p>
                </>
              ) : (
                <p>{record.requestNumber} returns to the register where it was.</p>
              )}

              {actionError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="attrition-requests"
        entityId={record.id}
      />
    </div>
  );
}

/** One banner for every state, not only the actionable ones. */
function bannerFor(record: AttritionRequestDetail): {
  icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' }>;
  title: string;
  description: string;
} {
  if (!record.isActive) {
    return {
      icon: Ban,
      title: 'Deleted',
      description: 'This request is hidden from the register. Restore it to put it back where it was.',
      
    };
  }

  const outstanding = record.documents.filter(
    (d) => d.reviewStatus === ATTRITION_REVIEW.notSubmitted || d.reviewStatus === ATTRITION_REVIEW.returned,
  ).length;
  const waiting = record.documents.filter((d) => d.reviewStatus === ATTRITION_REVIEW.forReview).length;
  const mine = record.documents.filter((d) => d.isMine && d.reviewStatus === ATTRITION_REVIEW.forReview).length;
  const uncleared = record.clearances.filter((c) => c.status !== ATTRITION_CLEARANCE.cleared).length;
  const mineUncleared = record.clearances.filter((c) => c.isMine && c.status !== ATTRITION_CLEARANCE.cleared).length;

  switch (record.status) {
    case ATTRITION_STATUS.pendingDocuments:
      return {
        icon: FileClock,
        title: 'Pending documents',
        description:
          `${outstanding} ${outstanding === 1 ? 'document still needs' : 'documents still need'} a file` +
          (waiting > 0 ? `, and ${waiting} ${waiting === 1 ? 'is' : 'are'} waiting on review.` : '.') +
          ' A returned document needs a new file.',
        
      };
    case ATTRITION_STATUS.forReview:
      return {
        icon: ClipboardCheck,
        title: 'For review',
        description:
          mine > 0
            ? `${mine} ${mine === 1 ? 'document is' : 'documents are'} waiting on your review.`
            : mineUncleared > 0
              ? `${mineUncleared} ${mineUncleared === 1 ? 'unit you sign for has' : 'units you sign for have'} not cleared the employee yet.`
              : waiting > 0
                ? `Every document is submitted; ${waiting} ${waiting === 1 ? 'is' : 'are'} waiting on ${waiting === 1 ? 'its reviewer' : 'their reviewers'}.` +
                  (uncleared > 0 ? ` ${uncleared} ${uncleared === 1 ? 'unit has' : 'units have'} not cleared the employee yet.` : '')
                : `Every document is approved; ${uncleared} ${uncleared === 1 ? 'unit has' : 'units have'} not cleared the employee yet.`,

      };
    case ATTRITION_STATUS.completed:
      return {
        icon: CheckCircle2,
        title: 'Completed',
        description:
          (record.clearances.length > 0
            ? 'Every document is approved and every unit has cleared the employee.'
            : 'Every document is approved.') +
          ' Nothing on the employee’s 201 file has changed — that is a later step.',
        
      };
    case ATTRITION_STATUS.cancelled:
      return {
        icon: XCircle,
        title: 'Cancelled',
        description: `This request will not go forward: ${record.cancelReason ?? ''}`,
        
      };
    default:
      return { icon: FileClock, title: record.statusLabel, description: '' };
  }
}
